/**
 * Permission List Page
 * Read-only listing of permissions (managed via code/migrations)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Box, Chip, Alert, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Permission } from '../../types/rbac.types';
import { getPermissions, getAvailableModules, getAvailableActions } from '../../services/permissionService';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { InlineFilterBar, InlineSelect } from '../../components/common';
import type { ActiveChip } from '../../hooks/useFilterState';

const getModuleDisplayName = (module: string): string => {
  const moduleNames: Record<string, string> = {
    users: 'Usuários',
    rbac: 'RBAC',
    companies: 'Empresas',
    employees: 'Funcionários',
    departments: 'Setores',
    occupations: 'Cargos',
    professionals: 'Profissionais',
    providers: 'Prestadores',
    schedules: 'Agendamentos',
    appointments: 'Atendimentos',
    exams: 'Exames',
    reports: 'Relatórios',
    settings: 'Configurações',
    system: 'Sistema',
  };
  return moduleNames[module] || module;
};

const getActionColor = (action: string): 'primary' | 'success' | 'warning' | 'error' | 'info' => {
  switch (action) {
    case 'create':
      return 'success';
    case 'read':
      return 'info';
    case 'update':
      return 'warning';
    case 'delete':
      return 'error';
    default:
      return 'primary';
  }
};

const columns: DataTableColumn<Permission>[] = [
  {
    id: 'name',
    label: 'Nome',
    primary: true,
    render: (permission) => (
      <Tooltip title={permission.name} arrow placement="right">
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {permission.displayName}
          </Typography>
          {permission.description && (
            <Typography variant="caption" color="text.secondary">
              {permission.description}
            </Typography>
          )}
        </Box>
      </Tooltip>
    ),
  },
  {
    id: 'module',
    label: 'Módulo',
    render: (permission) => (
      <Chip label={getModuleDisplayName(permission.module)} size="small" variant="outlined" />
    ),
  },
  {
    id: 'action',
    label: 'Ação',
    align: 'center',
    render: (permission) => (
      <Chip label={permission.action} size="small" color={getActionColor(permission.action)} />
    ),
  },
  {
    id: 'status',
    label: 'Status',
    align: 'center',
    render: (permission) => (
      <Chip
        label={permission.isActive ? 'Ativo' : 'Inativo'}
        size="small"
        color={permission.isActive ? 'success' : 'default'}
      />
    ),
  },
];

const PermissionListPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [moduleFilter, setModuleFilter] = useState<string>('');
  const [actionFilter, setActionFilter] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [availableModules, setAvailableModules] = useState<string[]>([]);
  const [availableActions, setAvailableActions] = useState<string[]>([]);

  const loadAvailableFilters = useCallback(async () => {
    try {
      const [modulesResponse, actionsResponse] = await Promise.all([
        getAvailableModules(),
        getAvailableActions(),
      ]);
      setAvailableModules(modulesResponse.data);
      setAvailableActions(actionsResponse.data);
    } catch (err: unknown) {
      console.error('Error loading filters:', err);
    }
  }, []);

  const loadPermissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getPermissions(
        {
          search: searchTerm ?? undefined,
          module: moduleFilter ?? undefined,
          action: actionFilter ?? undefined,
        },
        { page: page + 1, limit: rowsPerPage, sortBy: 'module', sortOrder: 'ASC' }
      );

      setPermissions(response.data);
      setTotalRows(response.meta.total);
    } catch (err: unknown) {
      console.error('Error loading permissions:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar permissões');
      enqueueSnackbar('Erro ao carregar permissões', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, moduleFilter, actionFilter, enqueueSnackbar]);

  useEffect(() => {
    loadAvailableFilters();
  }, [loadAvailableFilters]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(0);
  };

  const handleModuleFilterChange = (value: string) => {
    setModuleFilter(value);
    setPage(0);
  };

  const handleActionFilterChange = (value: string) => {
    setActionFilter(value);
    setPage(0);
  };

  const handleClearAll = () => {
    setSearchTerm('');
    setModuleFilter('');
    setActionFilter('');
    setPage(0);
  };

  const moduleOptions = availableModules.map((module) => ({
    value: module,
    label: getModuleDisplayName(module),
  }));

  const actionOptions = availableActions.map((action) => ({
    value: action,
    label: action,
  }));

  const chips: ActiveChip[] = [];
  if (moduleFilter) {
    chips.push({
      key: 'module',
      label: 'Módulo',
      value: getModuleDisplayName(moduleFilter),
      onDelete: () => handleModuleFilterChange(''),
    });
  }
  if (actionFilter) {
    chips.push({
      key: 'action',
      label: 'Ação',
      value: actionFilter,
      onDelete: () => handleActionFilterChange(''),
    });
  }

  return (
    <PageShell breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Permissões' }]} title="Permissões">
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <InlineFilterBar
          searchPlaceholder="Buscar permissões..."
          searchValue={searchTerm}
          onSearchChange={handleSearchChange}
          resultCount={totalRows}
          resultNoun="permissões"
          chips={chips}
          onClearAll={handleClearAll}
          loading={loading}
        >
          <InlineSelect
            label="Módulo"
            value={moduleFilter}
            options={moduleOptions}
            onChange={handleModuleFilterChange}
            emptyLabel="Todos"
            minWidth={150}
          />
          <InlineSelect
            label="Ação"
            value={actionFilter}
            options={actionOptions}
            onChange={handleActionFilterChange}
            emptyLabel="Todas"
            minWidth={120}
          />
        </InlineFilterBar>
      </Box>

      <DataTable
        columns={columns}
        rows={permissions}
        getRowId={(permission) => permission.id}
        loading={loading}
        emptyState={{
          title: 'Nenhuma permissão encontrada',
          description:
            'As permissões formam o catálogo fixo da plataforma e são atribuídas às funções.',
        }}
        pagination={{
          page,
          rowsPerPage,
          totalCount: totalRows,
          onPageChange: setPage,
          onRowsPerPageChange: (newRowsPerPage) => {
            setRowsPerPage(newRowsPerPage);
            setPage(0);
          },
        }}
      />
    </PageShell>
  );
};

export default PermissionListPage;
